/**
 *
 * @description  施工模板甘特图预览
 * @author xiongwei_zhang
 *
 * */

import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import { message } from 'antd';
import { Gantt } from 'gantt_commercial';
import DelLinkModal from './DelLinkModal';
// import LinksOfTask from './LinksOfTask';
import { editLink } from '&/api/personCenter/templateSettings';
import 'gantt_commercial/codebase/dhtmlxgantt.css';
import './PreviewGantt.scss';

function PreviewGantt(props) {
    const { dataSource, linkRender, onChange, hasEditPrim = true } = props;

    let gantt = React.useMemo(Gantt.getGanttInstance, []);

    const ganttContainer = useRef();

    const DEFAULT_CONFIG = {
        drag_project: false,
        columns: [
            {
                name: 'name',
                label: intl.get('projectSpace.project'),
                resize: true,
                width: '*',
                tree: true,
                onrender: item => {
                    return (
                        <div
                            title={item.name}
                            className={`text-no-wrap ${
                                item.parent > 0 ? 'is--child' : ''
                            }`}>
                            {item.parent === 0 && (
                                <i
                                    className={`filled icon-arrow icon-caret-down ${
                                        item.$open ? '' : 'is--rotate'
                                    }`}
                                    onClick={() => {
                                        gantt[item.$open ? 'close' : 'open'](
                                            item.id
                                        );
                                    }}></i>
                            )}
                            {item.name}
                        </div>
                    );
                },
            },
            {
                name: 'timeLimit',
                label: intl.get('projectSpace.times'),
                align: 'center',
                width: 64,
                onrender({ timeLimit }) {
                    return (
                        <div className="times-wrap">
                            <span>{timeLimit}天</span>
                        </div>
                    );
                },
            },
            {
                label: intl.get('templateSettings.decorateToDo'),
                name: 'decorateToDo',
                align: 'center',
                width: 118,
                resize: true,
                onrender: task => {
                    return linkRender(task.decorateToDo, task, 'decorateToDo');
                },
            },
            {
                label: intl.get('templateSettings.polling'),
                name: 'polling',
                align: 'center',
                resize: true,
                width: 118,
                onrender: (task, record) => {
                    return linkRender(task.polling, task, 'polling');
                },
            },
            {
                label: intl.get('templateSettings.acceptanceStandard'),
                name: 'acceptanceStandard',
                align: 'center',
                resize: true,
                width: 118,
                onrender: task => {
                    return linkRender(
                        task.acceptanceStandard,
                        task,
                        'acceptanceStandard'
                    );
                },
            },
        ],
        show_progress: false,
        min_grid_column_width: 1,
        autoscroll: true,
        duration_unit: 'day',
        duration_step: 1,
        fit_tasks: true,
        drag_move: false,
        drag_resize: false,
        order_branch: false, // 暂时关闭排序 原来的值 'marker'
        order_branch_free: false,
        task_date: '%Y-%m-%d',
        reorder_grid_columns: false,
        min_column_width: 30,
        details_on_dblclick: false,
        drag_progress: false,
        correct_work_time: true, // 允许将任务的开始和结束日期调整为工作时间(在拖动时)
        scales: [
            {
                unit: 'year',
                step: 12,
                format: (...arg) => {
                    return intl.get('templateSettings.ganttChart');
                },
            }, // %M %F %m %n
            {
                unit: 'day',
                step: 1,
                format: date => {
                    const startDate = moment().startOf('years');
                    const endDate = moment(date);
                    // return moment(date).dayOfYear()
                    return Math.abs(startDate.diff(endDate, 'days'));
                }, // %d %l %j %D %y %m | format: function (date)
            },
        ],
        external_render: {
            isElement: React.isValidElement,
            renderElement: ReactDOM.render,
        },
        select_task: true,
        bar_height: 20,
        scale_height: 41,
        row_height: 41,
        autofit: false,
        layout: {
            css: 'gantt_container',
            cols: [
                {
                    width: 560,
                    rows: [
                        {
                            view: 'grid',
                            scrollable: false,
                            scrollY: 'scrollVer',
                        },
                    ],
                },
                { resizer: true, width: 1 },
                {
                    rows: [
                        {
                            view: 'timeline',
                            scrollX: 'scrollHor',
                            scrollY: 'scrollVer',
                        },
                        {
                            view: 'scrollbar',
                            id: 'scrollHor',
                            group: 'horizontal',
                        },
                    ],
                },
                { view: 'scrollbar', id: 'scrollVer' },
            ],
        },
        keep_grid_width: true,
        container_resize_method: 'timeout',
        link_line_width: 1,
        link_arrow_size: 12,
        drag_links: false,
        type_renderers: {
            // 自定义“项目”类型横道图
            [gantt.config.types.project](task) {
                const main_el = document.createElement('div');
                main_el.setAttribute(gantt.config.task_attribute, task.id);
                let size = gantt.getTaskPosition(task);
                main_el.innerHTML = `<div class='gantt-task-progress-wrapper'><div class='ghost-item'></div></div><div class='project-content text-no-wrap'>${task.name}</div>`;
                main_el.className = 'custom-project type--unstart';
                main_el.style.left = size.left + 'px';
                main_el.style.top = size.top + 10 + 'px';
                main_el.style.width = size.width + 'px';
                return main_el;
            },
        },
        smart_rendering: true,
    };

    const DEFAULT_TEMPLATES = {
        task_class(start, end, task) {
            return 'type--unstart';
        },
        task_text(start, end, task) {
            return task.name;
        },
        //  设置左右角标
        /* rightside_text(start, end, task) {
            return task.rear.length;
        },
        leftside_text(start, end, task) {
            return task.pre ? 1 : null;
        }, */
        link_class(link) {
            return 'link--default';
        },
    };

    const changeDateScale = tasks => {
        const endList = tasks.map(item => item.timeEnd);
        const endDateNum = endList.length ? Math.max(...endList) : 2;
        const minDay = formatDate();
        const maxDay = moment(minDay)
            .add(endDateNum - 1, 'days')
            .format('YYYY-MM-DD');
        if (!maxDay) return;
        let { scale_unit } = gantt.getState();
        const min = new Date(minDay);
        const max = new Date(maxDay);
        gantt.config.end_date = gantt.calculateEndDate(max, 2, scale_unit);
        gantt.config.start_date = gantt.calculateEndDate(min, 0, scale_unit);
    };

    const linkInfoModal = useRef();

    const onDelLink = async id => {
        const link = gantt.getLink(id);
        return editLink({
            tempId: link.originId,
            sourceId: link.source,
            targetId: link.target,
            dap: 1,
        })
            .then(({ msg }) => {
                message.success(msg);
                onChange();
            })
            .catch(gantt.undo);
    };

    const linkDblClickHandler = (id, e) => {
        const link = gantt.getLink(id);
        linkInfoModal.current.show({
            position: {
                left: e.pageX - 76 + 'px',
                top: e.pageY - 76 + 'px',
                margin: 0,
            },
            sourceTask: gantt.getTask(link.source),
            targetTask: gantt.getTask(link.target),
            type: link.type,
            id,
        });
    };

    /*   const tableRef = useRef();

    //点击角标
    const clickHandler = (taskId, evt) => {
        const domHelpers = gantt.utils.dom;
        const currentTask = gantt.getTask(taskId);
        if (domHelpers.closest(evt.target, '.gantt_left')) {
            const { pre } = currentTask;
            const row = gantt.getTask(pre.linkPlanId);
            row.linkType = pre.type;
            row.linkId = pre.linkId;
            tableRef.current.show([row], 0);
        } else if (domHelpers.closest(evt.target, '.gantt_right')) {
            const { rear } = currentTask;
            const rows = rear.map(e => {
                const t = gantt.getTask(e.linkPlanId);
                t.linkType = e.type;
                t.linkId = e.linkId;
                return t;
            });
            tableRef.current.show(rows, 1);
        }

        return true;
    }; */

    // 缓存展开的任务
    const expandIds = useRef(new Set());
    const recordOpened = (type = 'add', id) => {
        expandIds.current[type](id);
    };

    const destroyGantt = () => {
        gantt.config.end_date = undefined;
        gantt.config.start_date = undefined;
        gantt.config.fit_tasks = true;
        gantt.clearAll();
        gantt.destructor();
        gantt = undefined;
    };

    const initGantt = () => {
        gantt.plugins({
            click_drag: true,
            drag_timeline: true,
        });
        gantt.i18n.setLocale('cn');
        Object.assign(gantt.config, DEFAULT_CONFIG);
        Object.assign(gantt.templates, DEFAULT_TEMPLATES);
        // gantt.attachEvent('onTaskClick', clickHandler);
        gantt.attachEvent('onLinkClick', linkDblClickHandler);
        gantt.attachEvent('onTaskOpened', recordOpened.bind(null, 'add'));
        gantt.attachEvent('onTaskClosed', recordOpened.bind(null, 'delete'));
        gantt.init(ganttContainer.current);
    };

    useEffect(() => {
        initGantt();
        return destroyGantt;
    }, []);

    const formatDate = (number, type) => {
        const targetDate = moment().startOf('year');
        return type
            ? targetDate.add(number, 'days')[`${type}Of`]('day').toDate()
            : targetDate.toDate();
    };

    // 把任务转换成甘特图需要的格式数据
    const formatTask = (task, other) => {
        const { level } = task;

        const result = {
            ...other,
            ...task,
            parent: level === '1' ? 0 : task.pid,
            type: ['project', 'task'][level - 1],
            start_date: formatDate(task.timeAt, 'start'),
            end_date: formatDate(task.timeEnd, 'end'),
        };
        return result;
    };

    // 数据更新
    const updateGantt = () => {
        gantt.clearAll();
        const { son: stages } = dataSource;
        if (!stages.length) return;
        const tasks = [];
        for (const item of stages) {
            if (item.son) {
                for (const sonItem of item.son) tasks.push(formatTask(sonItem));
            }

            const others = dataSource.tableData.find(e => e.id === item.id);
            tasks.push(formatTask(item, others || {}));
        }

        changeDateScale(tasks);
        gantt.parse({
            tasks,
            links: dataSource.links,
        });
        // 刷新数据后重新展开 "更新前展开任务"
        if (expandIds.current.size) {
            const ids = expandIds.current.values();
            for (const id of ids) gantt.open(id);
        }
        gantt.render();
    };

    useEffect(updateGantt, [dataSource]);

    return (
        <>
            <div ref={ganttContainer} className="template-gantt-el" />

            <DelLinkModal
                ref={linkInfoModal}
                hasEditPrim={hasEditPrim}
                onDelLink={onDelLink}
            />
            {/* <LinksOfTask
                hasEditPrim={hasEditPrim}
                ref={tableRef}
                onDelLink={onDelLink}
            /> */}
        </>
    );
}

export default PreviewGantt;
